import {
  CAN_CREATE_NEW_USERS,
  CLASS_ID,
  DISABLE_MEMES,
  ENABLE_TIMER,
  GRAPHICAL_LOGIN,
  SHUFFLE_ANSWERS,
  SHUFFLE_SCREENS,
} from "play/play-constants"
import { SessionMode } from "play/play-model"
import { Variant } from "models/tables"

export type StringSelectedCallback = (x: string) => any
export type Callback = (...args: any[]) => any

export interface LoginData {
  email: string
  password: string
}

export const nvo2022CampaignId = "E59F703C-E941-4BCD-8B78-67B9B6885F84"
export const nvo2020CampaignId = "E59F703C-E941-4BCD-8B78-67B9B6885F82"
export const emptyGuid = "00000000-0000-0000-0000-000000000000"

export const MAX_FILE_SIZE = 10000000 // 10mb

export const MAX_FILE_SIZE_TEXT = "10mb"

// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#target
export type AnchorTargetType = "_self" | "_blank" | "_parent" | "_top"

// https://imagemagick.org/script/formats.php
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Complete_list_of_MIME_types
export const SUPPORTED_IMAGES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]
export const SUPPORTED_IMAGES_ACCEPT = SUPPORTED_IMAGES.join(", ")
export const SUPPORTED_IMAGES_EXTENSION_LIST = ".png, .jpeg, .gif, .webp"
export const SUPPORTED_IMAGES_REGEX = /((\.png)|(\.jpe?g)|(\.gif)|(\.webp))$/i

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // $& means the whole matched string
}

export function toggleInArray<T>(item: T, arr: Array<T>) {
  const idx = arr.indexOf(item)
  if (idx == -1) {
    arr.push(item)
  } else {
    arr.splice(idx, 1)
  }
  return arr
}

export interface QueryStringHash {
  [key: string]: string
}

export function getQueryString(): QueryStringHash {
  const qsRaw = location.search.length > 1 ? location.search.slice(1) : ""
  const qs: QueryStringHash = qsRaw.split("&").reduce((acc, x) => {
    const sp = x.split("=")
    if (sp.length == 2) {
      acc[sp[0]] = decodeURIComponent(sp[1])
    } else {
      acc[sp[0]] = ""
    }
    return acc
  }, {} as QueryStringHash)
  return qs
}

class QueryString {
  private _qs: QueryStringHash

  constructor() {
    this._qs = getQueryString()
  }

  getItem(key: string): string | null {
    if (this._qs.hasOwnProperty(key)) {
      return this._qs[key]
    } else {
      return null
    }
  }

  getBoolean(key: string): boolean {
    if (this._qs.hasOwnProperty(key)) {
      const val = this._qs[key].toLocaleLowerCase()
      return val === "1" || val === "yes" || val === "true"
    }
    return false
  }

  getConverted<T>(key: string, converter: (val: string | null) => T): T {
    if (this._qs.hasOwnProperty(key)) {
      const val = this._qs[key]
      return converter(val)
    }
    return converter(null)
  }

  hasItem(key: string): boolean {
    const has = this._qs.hasOwnProperty(key)
    return has
  }
}

export const query = new QueryString()

// export function areArraysEqual<T>(lhs: T[], rhs: T[]): boolean {
//   if (lhs.length != rhs.length) return false;

//   var d1 = _.difference(lhs, rhs);

//   return d1.length == 0;
// }

/**
 * Convert hash to a string. Property names and values will be URL-encoded. No leading '?'
 * @param hash Object whose properties will be converted to query string
 * @returns Query string, without leading '?'
 */
export function buildQuery(hash: QueryStringHash) {
  const result = Object.getOwnPropertyNames(hash)
    .filter((key) => hasText(key) && hasText(hash[key]))
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(hash[key])}`)
    .join("&")
  return result
}

/**
 * Append query string to an url
 * @param url Url
 * @param hash Object with key-value pairs to be used to build a query string
 * @returns Url with url-encoded key-value pairs, formatted as query string
 */
export function withQuery(url: string, hash?: any) {
  const items: string[] = []

  if (hash) {
    for (let [key, val] of Object.entries(hash)) {
      if (val) {
        let str: string

        if (Array.isArray(val)) {
          if (val.length) {
            str = val.join(",")
          } else {
            str = ""
          }
        } else {
          str = (val + "").trim()
        }

        if (str.length) {
          items.push(`${encodeURIComponent(key)}=${encodeURIComponent(str)}`)
        }
      }
    }
  }

  if (items.length) {
    return `${url}?${items.join("&")}`
  }

  return url
}

export function isGuid(val: string | null | undefined) {
  // should match '00000000-0000-0000-0000-000000000000' and '{00000000-0000-0000-0000-000000000000}'
  const guidRegex = /^(([a-fA-F0-9]){8}-\2{4}-\2{4}-\2{4}-\2{12})|(\{\1\})$/

  if (typeof val === "string" && guidRegex.test(val)) {
    return true //val != emptyGuid;
  } else {
    return false
  }
}

export function isNonEmptyGuid(val: string | null | undefined) {
  const isTypeGuid = isGuid(val)
  return isTypeGuid && val !== emptyGuid
}

/**
 * Orders a screen's items (answers, pairs, categories, ellipses) the way the
 * server does (ChildOrder): by ordinal, which is fixed when an item is
 * created, then by id for two created at the same moment.
 */
export function creationOrder(
  a: { ordinal: number; id?: string },
  b: { ordinal: number; id?: string }
): number {
  if (a.ordinal !== b.ordinal) return a.ordinal - b.ordinal
  const x = a.id ?? ""
  const y = b.id ?? ""
  return x < y ? -1 : x > y ? 1 : 0
}

/** The list with the item added in creation order (see creationOrder). */
export function withInCreationOrder<T extends { ordinal: number; id?: string }>(
  items: T[],
  item: T
): T[] {
  return [...items, item].sort(creationOrder)
}

export function hasText(val: string | null | undefined): boolean {
  return typeof val === "string" && val != null && val.trim().length > 0
}

export function nullOrEmpty(val: string | null | undefined): boolean {
  return val !== null && typeof val === "string" && val.length > 0
}

export function textOr(
  val: string | null | undefined,
  fallback: string
): string {
  if (hasText(val)) {
    return val!
  } else {
    return fallback
  }
}

export function trimmedTextAt(val: string, maxLength: number): string {
  if (val.length > maxLength) {
    return val.substring(0, maxLength)
  } else {
    return val
  }
}

export function htmlOr(
  val: string | null | undefined,
  fallback: string
): string {
  if (hasText(val)) {
    const node = document.createElement("div")
    node.innerHTML = val!

    const hasText = node.innerText.length > 0
    node.remove()

    if (hasText) {
      return val!
    }
  }

  return fallback
}

export function buildStartClassroomGameUrl(opts: {
  classId?: string | null
  lessonId: string
  disableMemes?: boolean
  enableTimer?: boolean
  enableGraphicalLogin?: boolean
  enableNewUserRegistration: boolean
  shuffleAnswers: boolean
  shuffleScreens: boolean
}) {
  const id = encodeURIComponent(opts.lessonId)

  const qs = {} as any
  qs[DISABLE_MEMES] = opts.disableMemes
  qs[ENABLE_TIMER] = opts.enableTimer
  qs[CLASS_ID] = opts.classId
  qs[GRAPHICAL_LOGIN] = opts.enableGraphicalLogin
  qs[CAN_CREATE_NEW_USERS] = opts.enableNewUserRegistration
  qs[SHUFFLE_ANSWERS] = opts.shuffleAnswers
  qs[SHUFFLE_SCREENS] = opts.shuffleScreens

  return withQuery(
    `${location.protocol}//${location.host}/${lang}/play/start/${id}`,
    qs
  )
}

export function buildReturnUrl(
  additionalQuery?: QueryStringHash | undefined
): string {
  const existingQueryString = getQueryString()
  const queryString =
    typeof additionalQuery !== "undefined"
      ? { ...existingQueryString, ...additionalQuery }
      : existingQueryString

  const returnUrl = encodeURIComponent(
    withQuery(location.pathname, queryString)
  )
  return returnUrl
}

const lang = getHtmlLanguage()

/**
 * Build login URL by preserving the current query string and hash
 * @param additionalQuery Object with key-value pairs to add to the return url that will override the current query string
 * @returns Login url that will make sure we are redirected to the current url when login succeeds
 */
export function buildLoginUrl(additionalQuery?: QueryStringHash): string {
  const returnUrl = buildReturnUrl(additionalQuery)
  return withQuery(`/${lang}/account/login`, {
    returnUrl,
  })
}

export function buildLoginUrlWithGameCode(gameCode: string): string {
  return `/${lang}/account/login?gameCode=${gameCode}`
}

export function buildRegisterStudentUrl(
  additionalQuery?: QueryStringHash
): string {
  const returnUrl = buildReturnUrl(additionalQuery)
  return `/${lang}/account/student?returnUrl=${returnUrl}`
}

export function buildLogoutUrl(additionalQuery?: QueryStringHash): string {
  const returnUrl = buildReturnUrl(additionalQuery)
  return `/${lang}/account/logout?returnUrl=${returnUrl}`
}

/**
 * Access to cookies in API simiar to localStorage
 */
export const cookie = {
  /**
   * Set cookie by name
   * @param name Cookie name
   * @param value Cookie value
   * @param hours Hours, defaults to 1
   */
  setItem(name: string, value: string, hours = 1) {
    let expires: string = ""
    if (hours) {
      const date = new Date()
      date.setTime(date.getTime() + hours * 60 * 60 * 1000)
      expires = "; expires=" + date.toUTCString()
    }
    document.cookie = `${name}=${value}${expires}; path=/;SameSite=Lax`
  },

  /**
   * Retrieve a cookie by name
   * @param name Cookie name
   * @returns Value for the cookie, or null if not found
   */
  getItem(name: string) {
    var nameEQ = name + "="
    var ca = document.cookie.split(";")
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i]
      while (c.charAt(0) == " ") {
        c = c.substring(1, c.length)
      }
      if (c.indexOf(nameEQ) == 0) {
        var result = c.substring(nameEQ.length, c.length)
        return result
      }
    }
    return null
  },

  /**
   * Delete a cookie by name
   * @param name Cookie name
   */
  removeItem(name: string) {
    cookie.setItem(name, "", -1)
  },
}

export function getBasename() {
  let result = "/"
  const baseTags = document.getElementsByTagName("base")
  if (baseTags.length > 0) {
    const base = baseTags.item(baseTags.length - 1)
    const href = base?.getAttribute("href")
    if (href) {
      result = href
    }
  }
  return result
}

export function getHtmlLanguage(): string {
  const html = document.documentElement
  const result = html.getAttribute("lang") || "en"
  return result
}

export function getHtmlVariant(): Variant {
  const html = document.documentElement
  const result = (html.getAttribute("data-variant") || "School")
    .trim()
    .toLocaleLowerCase()
  if ("business" === result || "1" === result) {
    return Variant.Business
  }

  return Variant.School
}

export function random(min: number, max: number) {
  const result = min + Math.random() * (max - min)
  return Math.floor(result)
}

export function numberOr(val: number | undefined, ifEmpty: number): number {
  if (typeof val === "number") {
    return val
  } else {
    return ifEmpty
  }
}

export function boolOr(val: boolean | undefined, ifEmpty: boolean) {
  return typeof val === "boolean" ? val : ifEmpty
}

/**
 * Tries to parse a string input into bool, returning defaultValue on failure
 *
 * @param val String input to parse
 * @param defaultValue Value that should be returned on failure
 */
export function parsedBoolOr(
  val: string | null | undefined,
  defaultValue: boolean
): boolean {
  if (typeof val === "undefined" || val === null) return defaultValue

  const trimmed = val.trim()

  return /^((yes)|(1)|(true))$/i.test(trimmed)
}

export function valueOr<T>(val: T | null | undefined, defaultValue: T): T {
  if (typeof val !== "undefined" && val !== null) {
    return val
  } else {
    return defaultValue
  }
}

export function enqueue<T>(array: T[], item: T) {
  array.push(item)
}

export function peekDequeue<T>(array: T[]): T | null {
  if (array.length > 0) {
    return array[array.length - 1]
  } else {
    return null
  }
}

export function dequeue<T>(array: T[]): T | null {
  if (array.length > 0) {
    const removed = array.splice(array.length - 1, 1)
    return removed[0]
  } else {
    return null
  }
}

/**
 * Format a string in a way similar to .NET's string.Format
 *
 * @param fmt Format string, containing numbered placeholders (e.g. {0}) for arguments
 * @param args Arguments in placeholders
 *
 * @returns Formatted string
 */
export function format(fmt: string, ...args: any[]) {
  let countReplaced = 0

  const result = fmt.replace(/{(\d+)}/g, function (match, number) {
    countReplaced++
    return typeof args[number] != "undefined" ? args[number] : match
  })

  if (countReplaced !== args.length) {
    throw `Number of arguments ${args.length} different than number of placeholders ${result}`
  }

  return result
}

/**
 * Parse array in the form of |value1|value2| or value1|value2|value3 and return it as array of strings
 *
 * @param input Array packed by | as delimiter
 * @returns Formatted string
 */
export function parseArray(input: string): string[] {
  return input.split("|").filter(hasText)
}

/**
 * Build className by an array of simple values cast to strings, or objects whose keys are
 * considered CSS classes if their values are truthy
 *
 * If a parameter is String and it contains spaces, those are treated as speparate classes
 *
 * @param params any[] Different classes to add
 * @returns String CSS class combined of all parameters
 */
export function classNames(...params: any[]): string {
  let classes: string[] = []
  for (let p of params) {
    if (p === null) {
      continue
    }

    switch (typeof p) {
      case "bigint":
      case "number":
      case "boolean":
      case "symbol": {
        const val = p.toString()
        if (val.length > 0) {
          classes.push(p.toString())
        }
        break
      }
      case "string": {
        const parts = p.split(" ").filter((x) => x.length > 0)
        classes = classes.concat(parts)
        break
      }
      case "object": {
        for (let [key, value] of Object.entries(p)) {
          if (!!value) {
            classes.push(key)
          }
        }
        break
      }
    }
  }
  return classes.join(" ")
}

export interface Point {
  x: number
  y: number
}

export function emptyPoint(): Point {
  return { x: 0, y: 0 }
}

export interface Size {
  w: number
  h: number
}

export function emptySize(): Size {
  return { w: 0, h: 0 }
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

//  https://stackoverflow.com/questions/1480133/how-can-i-get-an-objects-absolute-position-on-the-page-in-javascript
export function absoluteOffset(element: Element): Point {
  let top = 0,
    left = 0

  let e: any | null | undefined = element
  while (e) {
    top += e.offsetTop || 0
    left += e.offsetLeft || 0
    e = e.offsetParent
  }

  return { x: left, y: top }
}

export function absoluteOffsetRelativeTo(
  needle: Element,
  parent: Element
): Point {
  let top = 0,
    left = 0
  let e: any | null | undefined = needle

  while (!!e) {
    top += e.offsetTop || 0
    left += e.offsetLeft || 0
    e = e.offsetParent
    if (parent.isEqualNode(e)) {
      break
    }
  }
  return { x: left, y: top }
}

/**
 * Find the first parent element that has position: "relative", or return document.body if none is found
 *
 * @param element Element whose DOM tree to traverse
 * @return First element with position: "relative" or document.body
 */
export function findFirstRelativeParent(element: HTMLElement): HTMLElement {
  let container: HTMLElement | null = null

  let parent = element.parentElement
  while (parent !== null) {
    const style = window.getComputedStyle(parent)
    if ("RELATIVE" === style.position.toUpperCase()) {
      container = parent
      break
    }
    parent = parent.parentElement
  }

  if (container === null) {
    container = document.body
  }

  return container
}

export function flatten<T>(...arrays: T[][]): T[] {
  let final: T[] = []
  for (let arr of arrays) {
    final = final.concat(arr)
  }
  return final
}

export function clamp(n: number, min: number, max: number) {
  if (n < min) {
    return min
  } else if (n > max) {
    return max
  } else {
    return n
  }
}

export function findWithIndex<T>(
  array: T[],
  predicate: (item: T) => boolean
): { index: number; item: T | null } {
  const len = array.length
  for (let index = 0; index < len; index++) {
    const item = array[index]
    const found = predicate(item)
    if (found) {
      return { index, item }
    }
  }

  return { index: -1, item: null }
}

export function isOverflown(element: HTMLElement): boolean {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  )
}

export function buildChangeLanguageUrl() {
  const loc = window.location
  const lang = getHtmlLanguage()
  const otherLocale = lang === "bg" ? "en" : "bg"
  const urlAfterLocale = loc.pathname.length > 3 ? loc.pathname.slice(3) : "/"
  const newUrl = `${loc.protocol}//${loc.host}/${otherLocale}${urlAfterLocale}`
  return newUrl
}

// https://developer.mozilla.org/en-US/docs/Glossary/Base64#solution_2_%E2%80%93_rewriting_atob_and_btoa_using_typedarrays_and_utf-8
function b64ToUint6(nChr: number) {
  return nChr > 64 && nChr < 91
    ? nChr - 65
    : nChr > 96 && nChr < 123
      ? nChr - 71
      : nChr > 47 && nChr < 58
        ? nChr + 4
        : nChr === 43
          ? 62
          : nChr === 47
            ? 63
            : 0
}

function base64DecToArr(sBase64: string, nBlocksSize: number) {
  const sB64Enc = sBase64.replace(/[^A-Za-z0-9+/]/g, "")
  const nInLen = sB64Enc.length
  const nOutLen = nBlocksSize
    ? Math.ceil(((nInLen * 3 + 1) >> 2) / nBlocksSize) * nBlocksSize
    : (nInLen * 3 + 1) >> 2
  const taBytes = new Uint8Array(nOutLen)

  let nMod3
  let nMod4
  let nUint24 = 0
  let nOutIdx = 0
  for (let nInIdx = 0; nInIdx < nInLen; nInIdx++) {
    nMod4 = nInIdx & 3
    nUint24 |= b64ToUint6(sB64Enc.charCodeAt(nInIdx)) << (6 * (3 - nMod4))
    if (nMod4 === 3 || nInLen - nInIdx === 1) {
      nMod3 = 0
      while (nMod3 < 3 && nOutIdx < nOutLen) {
        taBytes[nOutIdx] = (nUint24 >>> ((16 >>> nMod3) & 24)) & 255
        nMod3++
        nOutIdx++
      }
      nUint24 = 0
    }
  }

  return taBytes
}

/* Base64 string to array encoding */
function uint6ToB64(nUint6: number) {
  return nUint6 < 26
    ? nUint6 + 65
    : nUint6 < 52
      ? nUint6 + 71
      : nUint6 < 62
        ? nUint6 - 4
        : nUint6 === 62
          ? 43
          : nUint6 === 63
            ? 47
            : 65
}

export function base64EncArr(aBytes: Uint8Array) {
  let nMod3 = 2
  let sB64Enc = ""

  const nLen = aBytes.length
  let nUint24 = 0
  for (let nIdx = 0; nIdx < nLen; nIdx++) {
    nMod3 = nIdx % 3
    if (nIdx > 0 && ((nIdx * 4) / 3) % 76 === 0) {
      sB64Enc += "\r\n"
    }

    nUint24 |= aBytes[nIdx] << ((16 >>> nMod3) & 24)
    if (nMod3 === 2 || aBytes.length - nIdx === 1) {
      sB64Enc += String.fromCodePoint(
        uint6ToB64((nUint24 >>> 18) & 63),
        uint6ToB64((nUint24 >>> 12) & 63),
        uint6ToB64((nUint24 >>> 6) & 63),
        uint6ToB64(nUint24 & 63)
      )
      nUint24 = 0
    }
  }
  return (
    sB64Enc.substr(0, sB64Enc.length - 2 + nMod3) +
    (nMod3 === 2 ? "" : nMod3 === 1 ? "=" : "==")
  )
}

/* UTF-8 array to JS string and vice versa */

export function UTF8ArrToStr(aBytes: Uint8Array) {
  let sView = ""
  let nPart
  const nLen = aBytes.length
  for (let nIdx = 0; nIdx < nLen; nIdx++) {
    nPart = aBytes[nIdx]
    sView += String.fromCodePoint(
      nPart > 251 && nPart < 254 && nIdx + 5 < nLen /* six bytes */
        ? /* (nPart - 252 << 30) may be not so safe in ECMAScript! So…: */
          (nPart - 252) * 1073741824 +
            ((aBytes[++nIdx] - 128) << 24) +
            ((aBytes[++nIdx] - 128) << 18) +
            ((aBytes[++nIdx] - 128) << 12) +
            ((aBytes[++nIdx] - 128) << 6) +
            aBytes[++nIdx] -
            128
        : nPart > 247 && nPart < 252 && nIdx + 4 < nLen /* five bytes */
          ? ((nPart - 248) << 24) +
            ((aBytes[++nIdx] - 128) << 18) +
            ((aBytes[++nIdx] - 128) << 12) +
            ((aBytes[++nIdx] - 128) << 6) +
            aBytes[++nIdx] -
            128
          : nPart > 239 && nPart < 248 && nIdx + 3 < nLen /* four bytes */
            ? ((nPart - 240) << 18) +
              ((aBytes[++nIdx] - 128) << 12) +
              ((aBytes[++nIdx] - 128) << 6) +
              aBytes[++nIdx] -
              128
            : nPart > 223 && nPart < 240 && nIdx + 2 < nLen /* three bytes */
              ? ((nPart - 224) << 12) +
                ((aBytes[++nIdx] - 128) << 6) +
                aBytes[++nIdx] -
                128
              : nPart > 191 && nPart < 224 && nIdx + 1 < nLen /* two bytes */
                ? ((nPart - 192) << 6) + aBytes[++nIdx] - 128
                : /* nPart < 127 ? */ /* one byte */
                  nPart
    )
  }
  return sView
}

export function strToUTF8Arr(sDOMStr: string) {
  let aBytes
  let nChr: number = 0
  const nStrLen = sDOMStr.length
  let nArrLen = 0

  /* mapping… */
  for (let nMapIdx = 0; nMapIdx < nStrLen; nMapIdx++) {
    nChr = sDOMStr.codePointAt(nMapIdx)!

    if (nChr > 65536) {
      nMapIdx++
    }

    nArrLen +=
      nChr < 0x80
        ? 1
        : nChr < 0x800
          ? 2
          : nChr < 0x10000
            ? 3
            : nChr < 0x200000
              ? 4
              : nChr < 0x4000000
                ? 5
                : 6
  }

  aBytes = new Uint8Array(nArrLen)

  /* transcription… */
  let nIdx = 0
  let nChrIdx = 0
  while (nIdx < nArrLen) {
    nChr = sDOMStr.codePointAt(nChrIdx)!
    if (nChr < 128) {
      /* one byte */
      aBytes[nIdx++] = nChr
    } else if (nChr < 0x800) {
      /* two bytes */
      aBytes[nIdx++] = 192 + (nChr >>> 6)
      aBytes[nIdx++] = 128 + (nChr & 63)
    } else if (nChr < 0x10000) {
      /* three bytes */
      aBytes[nIdx++] = 224 + (nChr >>> 12)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
    } else if (nChr < 0x200000) {
      /* four bytes */
      aBytes[nIdx++] = 240 + (nChr >>> 18)
      aBytes[nIdx++] = 128 + ((nChr >>> 12) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
      nChrIdx++
    } else if (nChr < 0x4000000) {
      /* five bytes */
      aBytes[nIdx++] = 248 + (nChr >>> 24)
      aBytes[nIdx++] = 128 + ((nChr >>> 18) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 12) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
      nChrIdx++
    } /* if (nChr <= 0x7fffffff) */ else {
      /* six bytes */
      aBytes[nIdx++] = 252 + (nChr >>> 30)
      aBytes[nIdx++] = 128 + ((nChr >>> 24) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 18) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 12) & 63)
      aBytes[nIdx++] = 128 + ((nChr >>> 6) & 63)
      aBytes[nIdx++] = 128 + (nChr & 63)
      nChrIdx++
    }
    nChrIdx++
  }

  return aBytes
}

export function scrollIntoViewCenter(element: HTMLElement) {
  // https://stackoverflow.com/questions/8922107/javascript-scrollintoview-middle-alignment
  const container = element.offsetParent
  if (!container) {
    console.warn(
      "offsetParent is not set! Set position: relative (or otherwise make it a positioned element) " +
        "to the scrolling parent"
    )
    element.scrollIntoView()
    return
  }

  const absoluteElementTop = element.offsetTop + element.clientHeight / 2
  const middle = absoluteElementTop - container.clientHeight / 2
  container.scrollTo(0, middle)
}

export function titleCase(input: string): string {
  if (input.length > 1) {
    return input.charAt(0).toUpperCase() + input.substring(1)
  } else {
    return input
  }
}

export function asPromise<T = void>(
  maybePromise: void | undefined | null | Promise<T>
): Promise<T> | null {
  if (maybePromise === null) return null

  if (maybePromise instanceof Promise) {
    return maybePromise
  } else if (typeof maybePromise === "object") {
    if (
      typeof (maybePromise as any).then === "function" &&
      typeof (maybePromise as any).catch === "function"
    ) {
      return maybePromise as Promise<T>
    }
  }

  return null
}

export function awaitMilliseconds(milliseconds: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (milliseconds < 0) reject("milliseconds is negative")
    if (milliseconds === 0) resolve()

    setTimeout(() => {
      resolve()
    }, milliseconds)
  })
}

export const nameof = <T>(name: Extract<keyof T, string>): string => name

export function contains(rt: DOMRect, x: number, y: number): boolean
export function contains(rt: DOMRect, pt: { x: number; y: number }): boolean
export function contains(any: any): boolean {
  let rt: { left: number; right: number; top: number; bottom: number } | null =
    null
  let x: number = 0
  let y: number = 0

  if (arguments.length === 3) {
    if (
      arguments[0] instanceof DOMRect &&
      typeof arguments[1] === "number" &&
      typeof arguments[2] === "number"
    ) {
      rt = arguments[0]
      x = arguments[1]
      y = arguments[1]
    }
  } else if (arguments.length === 2) {
    if (arguments[0] instanceof DOMRect) {
      rt = arguments[0]
      const pt = arguments[1]

      if (
        typeof pt === "object" &&
        pt !== null &&
        typeof pt["x"] === "number" &&
        typeof pt["y"] === "number"
      ) {
        x = pt.x
        y = pt.y
      }
    }
  }

  if (rt !== null) {
    return x >= rt.left && x <= rt.right && y >= rt.top && y <= rt.bottom
  } else {
    throw "Unknown arguments to contains"
  }
}

export function yesno(option: boolean): string {
  return option ? "yes" : "no"
}

export function createLogger(name: string) {
  function log(...args: any[]) {
    if (args.length > 0 && typeof args[0] === "string") {
      args[0] = `[${name}] ${args[0]}`
    } else {
      args.splice(0, 0, [`[${name}]`])
    }
    console.log.apply(console, args)
  }

  return log
}

export function parsePadding(padding: string | null | undefined): number {
  if (typeof padding !== "string") return 0

  padding = padding.trim()
  if (padding.length === 0) return 0

  const res = /([\+|\-]?\d+)px/.exec(padding)
  if (res === null || res.length !== 2) return 0

  return Number(res[1])
}

export function getElementPadding(el: Element) {
  const style = window.getComputedStyle(el)
  return {
    left: parsePadding(style.paddingLeft),
    right: parsePadding(style.paddingRight),
    top: parsePadding(style.paddingTop),
    bottom: parsePadding(style.paddingBottom),
  }
}

// https://stackoverflow.com/questions/5598743/finding-elements-position-relative-to-the-document
export function getElementCoords(elem: HTMLElement) {
  // crossbrowser version
  const box = elem.getBoundingClientRect()

  const body = document.body
  const docEl = document.documentElement

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft

  const clientTop = docEl.clientTop || body.clientTop || 0
  const clientLeft = docEl.clientLeft || body.clientLeft || 0

  const top = box.top + scrollTop - clientTop
  const left = box.left + scrollLeft - clientLeft

  return { top: Math.round(top), left: Math.round(left) }
}

interface MaybePreventDefault {
  preventDefault?: () => void
}

export function reactPreventDefault(e: MaybePreventDefault) {
  if (typeof e.preventDefault === "function") {
    e.preventDefault()
  }
  return false
}

export function getYoutubeId(url: string) {
  if (!url) return ""

  const regExp =
    /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/
  const match = url.match(regExp)
  if (match && match[2].length == 11) {
    return match[2]
  } else {
    return ""
  }
}

/**
 * Construct absolute URL to play page to join a game given a game code
 * @param code Game code
 */
export function playUrlWithCode(code: string): string {
  const lang = getHtmlLanguage()
  return `${location.protocol}//${location.host}/${lang}/play?code=${encodeURIComponent(code)}`
}

export function mapUpTo<T, U>(
  arr: T[],
  max: number,
  mapper: (item: T) => U
): U[] {
  let count = 0
  const res: U[] = []

  for (let item of arr) {
    if (count >= max) break

    const c = mapper(item)
    res.push(c)
    count++
  }

  return res
}

export interface GameCode {
  code: number
  sessionMode: SessionMode
  isNvo: boolean
}

export function parseGameCode(
  input: string | null | undefined
): GameCode | null {
  if (typeof input !== "string") return null

  let code = parseInt(input, 10)
  if (isNaN(code)) return null

  const isNvo = code === 1111 || code === 3333 || code === 5555 || code === 7777
  let sessionMode: SessionMode

  if (isNvo) {
    sessionMode = SessionMode.Homework
  } else {
    sessionMode = code % 2 === 0 ? SessionMode.Classroom : SessionMode.Homework
  }

  return {
    isNvo,
    code,
    sessionMode,
  }
}

export interface ConnectionId {
  gameCode: GameCode
  userKind: "Anonymous" | "Student" | "Teacher"
  userId: string | null
  playthroughId: string | null
  fullName: string | null
}

export function parseConnectionId(
  input: string | null | undefined
): ConnectionId | null {
  if (typeof input !== "string") return null

  let parts = input.split("_")
  if (parts.length !== 4 && parts.length !== 5) return null

  if (parts[0] !== "CID") return null

  const gameCode = parseGameCode(parts[1])
  if (!gameCode) return null

  let userKind: ConnectionId["userKind"]
  if (parts[2] === "S") {
    userKind = "Student"
  } else if (parts[2] === "T") {
    userKind = "Teacher"
  } else if (parts[2] === "A") {
    userKind = "Anonymous"
  } else {
    return null
  }

  if (gameCode.isNvo) {
    if (parts.length !== 5) return null

    if (userKind === "Student" || userKind === "Teacher") {
      if (parts[3].length === 0 || parts[4].length === 0) return null

      return {
        userKind,
        gameCode,
        fullName: null,
        userId: parts[3],
        playthroughId: parts[4],
      }
    } else {
      return {
        userKind,
        gameCode,
        playthroughId: parts[3],
        userId: null,
        fullName: parts[4],
      }
    }
  } else {
    if (parts.length !== 4) return null
    if (parts[3].length === 0) return null

    return {
      userKind,
      gameCode,
      fullName: userKind === "Anonymous" ? parts[3] : null,
      userId: userKind === "Anonymous" ? null : parts[3],
      playthroughId: null,
    }
  }
}

export function isValidEmail(input: string | undefined | null): boolean {
  if (!input) return false

  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(input)
}

export function isValidEmailOrEmptyString(input: string | undefined | null) {
  return isNullOrWhitespace(input) || isValidEmail(input)
}

export function isNullOrWhitespace(input: string | undefined | null): boolean {
  if (!input || input.length === 0) return true

  return /\s+/.test(input)
}

export function shuffleInPlace<T>(arr: T[]): void {
  if (arr.length <= 1) {
    return
  }

  const len = arr.length
  for (let i = 0; i < len; i++) {
    const j = random(0, len)
    if (i === j) continue

    const t = arr[i]
    arr[i] = arr[j]
    arr[j] = t
  }
}

export function range(upToExclusive: number): number[] {
  let result: number[] = []
  for (let i = 0; i < upToExclusive; i++) {
    result.push(i)
  }
  return result
}

export function atOrNull<T>(arr: T[], idx: number): T | null {
  if (idx < 0 || idx >= arr.length) return null

  return arr[idx]
}

export function letter(index: number): string {
  const letters = "abcdefghijklmnopqrstuvxyz"
  let result = letters.at(index % letters.length)!
  let x = index
  while (x > letters.length) {
    x /= letters.length
    result += letters.at(x % letters.length)!
  }
  return result
}

export function romanDigit(index: number): string {
  throw "TODO"
}
